import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { apiGateWay } from '../../layout/rmm/environments/environment'
import { BehaviorSubject, Observable , of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { TokenStorage } from '../../core/token.storage';
import { User } from '../../layout/workspace/workspace-list/interfaces/room';
@Injectable()
export class TenantService {
    public tenantNameSub = new BehaviorSubject('tenant');
    private SERVICETYPE = {
        'rocketstart': 'room',
        'marvel': 'user',
        'rmm': 'rmm',
        'scp': 'scpbase',
        'esignage': 'esignage'
    }
    constructor(private http: HttpClient, private token: TokenStorage) {
    }
    getTenantInfo(acessToken, skip = false): Observable<any> {
        // Skip calling the getTenantInfo API during login with SA and SSA users for EU.
        // Reason: SA (Super Admin) and SSA (Super Sharp Admin) are not tied to any specific tenant.
        // This placeholder ensures that related references don't break when the API is intentionally skipped.
        // For DA and Other logins we should call getTeantInfo API

        if (skip) {
            return of([{
                "customerContactEmail": "adminportal@ssphierarchy.invalid",
                "dealerCentric": {
                    "rmm": true
                },
                "domain": "ssphierarchy.invalid",
                "tier": {
                    "rmm": true
                }
            }],
            );
        }
        const URI = environment.rmpEndPointTenant.url;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + acessToken
            }),
        };
        return this.http.get<any>(URI, httpOptions);
    }

    // SHPLT-10731 fix: Caches the last tenantList response, keyed by (accessToken, servicePreviouslylogged).
    private tenantListCache: { key: string; response$: Observable<any> } | null = null;

    // call to tenantList api
    getTenantList(accessToken, servicePreviouslylogged = null): Observable<any> {
        const cacheKey = `${accessToken}::${servicePreviouslylogged}`;
        if (this.tenantListCache && this.tenantListCache.key === cacheKey) {
            return this.tenantListCache.response$;
        }
        const URI = `${environment.rmpEndPointUser.url}/guest/tenantList`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken,
                'Previous-Login-Service': servicePreviouslylogged || '',
            }),
        };
        // refCount: true so a failed request doesn't get stuck cached as an error forever.
        const response$ = this.http.get<any>(URI, httpOptions).pipe(shareReplay({ bufferSize: 1, refCount: true }));
        this.tenantListCache = { key: cacheKey, response$ };
        return response$;
    }

    // clears the cached tenantList response; call whenever the active tenant actually changes
    clearTenantListCache(): void {
        this.tenantListCache = null;
    }

    // call to switchTenant api
    switchTenant(tenant, accessToken, sspTenantId:any = null) {
        this.clearTenantListCache();
        const URI = `${environment.rmpEndPointUser.url}/guest/switchTenant`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'token': 'Bearer ' + accessToken,
            }),
        };
        const body: any = {
            tenant: tenant
        };
        if (environment.stackRegion!=='JP') {
            // add only if exists
            if (sspTenantId) {
                body.sspTenantId = sspTenantId;
            }
        }
        
        return this.http.post<any>(URI, body, httpOptions);
    }
    updateTargetService(accessToken, servicePreviouslylogged){
        const URI = `${environment.rmpEndPointUser.url}/updateTargetService`;
        const httpOptions ={
            headers:new HttpHeaders({
                'Content-Type': 'application/json',
                'token': 'Bearer ' + accessToken,
                'Selected-Service': servicePreviouslylogged,
                'Guest':'true'
            })
        }
        return this.http.patch<any>(URI,{}, httpOptions);
    }

    createTenantForSelectedProvider(provider, token, customInfo?): Observable<any> {
        const URI = environment.selectProviderURL.url;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'token': token,
            }),
        };
        const body = { 'provider': provider, 'customInfo': customInfo};
        return this.http.post<any>(URI, body, httpOptions);
    }

    getTenantInfoWithOutToken(token): Observable<any> {
        const URI = environment.selectProviderURL.url;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'token': token,
            }),
        };
        return this.http.get<any>(URI, httpOptions);
    }

    /**
     * Query the user details from DB
     * @param {any} queryOptions The query parameters as an object
     */
    queryUser(queryOptions: any, accessToken, isGuest: boolean = false, role: string = 'user'): Observable<any> {
        const queryString = this.jsonToQueryString(queryOptions);
        const queryUserUrl = `${environment.rmpEndPointUser.url}/users?${queryString}`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken,
            }),
        };
        // Add guest-specific headers if the user is a guest
        if (isGuest) {
            httpOptions.headers = httpOptions.headers
                .set('user-role', role)
                .set('guest', 'true');
        }
        return this.http.get<any>(queryUserUrl, httpOptions);
    }

    /**
     * Patch the record for the currently logged-in user in DB
     * @param {User} user The user instance
     */
    patchUser(userId: string, userInfo: any, accessToken): Observable<User> {
        const guest = 'false';
        const userRole = "user";
        const patchUserUrl = `${environment.rmpEndPointUser.url}/users/${userId}`;
        const httpOptions = {
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "user-role": userRole,
                Authorization: "Bearer " + accessToken,
                feature_type: this.SERVICETYPE[
                    this.token.getMarvelRMPService()
                ],
                'guest' : guest
            }),
        };
        return this.http.patch<User>(patchUserUrl, userInfo, httpOptions);
    }

    /**
     *  Convert an object to query string
     * @param {any} json The query params as an object
     */
    jsonToQueryString(json: any): String {
        const entries = Object.keys(json).map(key => {
            return encodeURIComponent(key) + '=' +
                encodeURIComponent(json[key]);
        });
        return entries.join('&');
    }


    getGuestTenantInfo(authCon): Observable<any> {
        const URI = environment.rmpEndPointTenant.url+ '/guest';
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'guest-authCon': authCon
            }),
        };
        return this.http.get<any>(URI, httpOptions);
    }

    /**
     * Update the record for the tenant in DB
     * @param {tenant} tenant The tenant instance
     */
    updateTenantName(tenant: any): Observable<any> {
        const URI = `${environment.rmpEndPointTenant.url}/name`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
        };
        return this.http.put<any>(URI, tenant, httpOptions);
    }

    getCalendarAccountInfo(): Observable<any> {
        const URI = environment.rmpEndPointUser.url + '/calendarAccount';
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
        };
        return this.http.get<any>(URI, httpOptions);
    }

    deleteCalendarAccount(email: Array<string>): Observable<any> {
        const URI = environment.rmpEndPointUser.url + '/calendarAccount';
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: { 'email': email },
        };
        return this.http.delete<any>(URI,httpOptions);
    }
    getTenantAccess(sspTenantId, dealerNumber): Observable<any> {
        // Use the correct endpoint path if needed
         const URI = environment.sspLicenseApi.url + '/hierarchy/tenantAccess';
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'dealernumber': dealerNumber,
                'ssptenantid': sspTenantId
            }),
        };
        return this.http.get<any>(URI, httpOptions);
    }
    updateTenantAccess(sspTenantId, dealerNumber, tenantAccess, rmmAdminRole): Observable<any> {
        const URI = environment.sspLicenseApi.url + '/hierarchy/tenantAccess';
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'dealernumber': dealerNumber,
                'ssptenantid': sspTenantId
            }),
        };

        // Build the body dynamically based on provided arguments
        const body: any = {};
        if (rmmAdminRole == 'IT Main') {
            body.allowServiceUserAccess = tenantAccess;
        }
        if (rmmAdminRole == 'Channel Admin') {
            body.allowSIICAAccess = tenantAccess;
        }

        return this.http.patch<any>(URI, body, httpOptions);
    }

    
    async getAccessTokenFromSessionId(sessionId) {
        const RMM_COMMON_API_URL = apiGateWay.baseRmmUrl;
        const RMM_SESSION_API = `${RMM_COMMON_API_URL}/ev1/api/useSession/${sessionId}`;
        try {
            const requestOptions = {
                method: 'GET',
                headers: {
                    'User-Agent': 'undici-stream-example',
                    'Content-Type': 'application/json'
                }
            };
            const response = await fetch(RMM_SESSION_API, requestOptions);
            const data = await response.json();

            if (response.status > 300) {
                console.error(`HTTP ${response.status} while getting access token`);
            }

            const accessToken = data.access_token;

            return { accessToken };
        }
        catch (error) {
            console.error(`Unable get device info url`, error);
        }
        return null;

    }
}